import { useSubscriptionPlans, useUpdatePlanAPI, useSubscriptionManageUrl } from "@arelia-components/SubscriptionDetails/useSubscriptionDetailsAPI";
import { SubscriptionPlan, SubscriptionPlanName } from "@arelia-types/subscriptions";
import { Box, Button, Dialog, DialogContent, DialogTitle, Typography, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import theme from "src/theme";
import SpinnerWrapper from "@arelia-components/ui-atoms/SpinnerWrapper";
import { useConfiguration } from "src/api/configuration";
import { toast } from "sonner";

type Props = {
  onClose: () => void,
  onSuccess: () => void,
  plan: SubscriptionPlan,
  isFreePlan: boolean
};

export default function ChangePlanDialog({ onClose, onSuccess, plan, isFreePlan }: Props) {
  const { mutate, isLoading } = useUpdatePlanAPI(onSuccess);
  const { data: plans, isLoading: plansLoading } = useSubscriptionPlans();
  const { data: config } = useConfiguration();
  const { mutate: loadManageSubUrl } = useSubscriptionManageUrl(() => {
    toast.error("Failed to cancel subscription.");
  });

  const updatePlan = (plan: SubscriptionPlan) => {
    mutate(plan);
  };

  return (
    <Dialog open={true} maxWidth={'md'} onClose={() => onClose()}>
      <DialogTitle>
        <Box sx={styles.header}>
          Change Plan
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={styles.dialogContent}>
        <SpinnerWrapper loading={plansLoading}>
          <Box sx={styles.plansRow}>
            {plans && plans.filter(p => {
              if (p.name === SubscriptionPlanName.Free) return false;
              if (p.name === SubscriptionPlanName.MySocialMyFans && !config?.flags.EnableMyFans) return false;
              return true;
            }).map(p => (
              <Box sx={styles.plan.wrapper(plan === p.name)} key={p.name}>
                <Box sx={styles.plan.content}>
                  <Typography variant="h2" sx={styles.plan.title}>{p.stripeName}</Typography>
                  <Typography sx={styles.plan.price}>{p.payment === 0 ? 'FREE' : `$${p.payment}`}</Typography>
                  <Typography sx={styles.plan.period}>{p.payment === 0 ? '' : 'Per Month'}</Typography>
                  <Box sx={styles.plan.divider}></Box>
                  <Typography variant="subtitle1" sx={styles.plan.description}>{p.featuresTitle}</Typography>
                  {p.features && <ul style={styles.plan.list}>
                    {p.features.map(f => <li key={f}>{f}</li>)}
                  </ul>}
                </Box>
                <Button
                  variant="outlined"
                  size="large"
                  disabled={plan === p.name || isLoading}
                  onClick={() => updatePlan(p.name)}
                >
                  {plan === p.name ? "Active" : (isLoading ? "Changing..." : "Select")}
                </Button>
              </Box>
            ))}
          </Box>
          {plan !== SubscriptionPlanName.Free && !isFreePlan && (
            <Box sx={styles.cancelBox}>
              <Typography sx={styles.cancelText}>
                We are sad to see you go, but if you need to cancel, click "Cancel Subscription"
              </Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                sx={styles.cancelButton}
                onClick={() => loadManageSubUrl()}
              >
                Cancel Subscription
              </Button>
            </Box>
          )}
        </SpinnerWrapper>
      </DialogContent>
    </Dialog>
  );
}

const styles = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  dialogContent: {
    display: "block",
    overflow: "unset"
  },
  plansRow: {
    display: "flex",
    flexDirection: "row",
    gap: "20px",
    justifyContent: "center",
    marginBottom: "32px"
  },
  plan: {
    wrapper: (isActive: boolean) => ({
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      width: "300px",
      border: `2px solid ${isActive ? theme.palette.primary.main : "#c7c8c9"}`,
      borderRadius: "10px",
      justifyContent: "space-between",
      backgroundColor: isActive ? "#f5f5f5" : "transparent"
    }),
    content: { display: "flex", flexDirection: "column", alignItems: "center" },
    title: { textAlign: "center" },
    price: { marginTop: "30px", fontSize: "28px" },
    period: { fontSize: "16px", fontStyle: "italic", minHeight: "24px" },
    divider: { height: "1px", width: "100%", backgroundColor: "#c7c8c9", margin: "20px 0" },
    description: { marginBottom: "16px" },
    list: { paddingLeft: "20px", margin: "0 0 20px 0" }
  },
  cancelBox: {
    width: "100%",
    marginTop: "0px",
    padding: "6px 12px",
    background: "#fff3e0",
    borderRadius: "6px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    border: "1px solid #ffd180",
    minHeight: "32px"
  },
  cancelText: {
    textAlign: "left",
    fontSize: "12px",
    color: "#6d4c41"
  },
  cancelButton: {
    minWidth: "80px",
    fontSize: "12px",
    padding: "2px 10px",
    borderRadius: "14px",
    fontWeight: 500
  }
};
